/**
 * A completude verificada do Caso (`TP-058`): `GET /casos/{id}/completude` e o aceite sem prova. A conta é a mesma da
 * trava do passo e do cartão do quadro, então qualquer escrita que mude documento, passo ou frente invalida esta chave.
 */

import { queryOptions, type QueryClient } from '@tanstack/react-query';
import type { Schemas } from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import { AO_VIVO } from '../api/casos';
import type { Camada, Completude, EstadoDoItem, ItemDaCompletude, TipoDeProva } from './tipos';

type Fio = Schemas['CompletudeResponse'];

export const chavesDeCompletude = {
  doCaso: (casoId: string) => ['casos', casoId, 'completude'] as const,
};

/**
 * O que muda quando o Caso muda (documento validado ou reaberto, aceite sem prova, passo fechado): a completude e,
 * porque o sistema fecha sozinho o passo da vez já todo provado (`D18`), o próprio Caso — o passo da frente, a linha do
 * tempo e o cartão no quadro. `jaAtualizada` é a parte que quem chama já gravou com a resposta da escrita.
 */
export function invalidarOQueAProvaMuda(
  cliente: QueryClient,
  casoId: string,
  jaAtualizada: 'documentos' | 'completude' | 'detalhe',
): Promise<void> {
  return cliente.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] === 'casos' &&
      ((queryKey[1] === casoId && queryKey[2] !== jaAtualizada) ||
        queryKey[1] === 'lista' ||
        queryKey[1] === 'captacao'),
  });
}

function mapearItem(f: Schemas['ItemDaCompletudeResponse']): ItemDaCompletude {
  return {
    chave: f.chave ?? '',
    rotulo: f.rotulo ?? f.chave ?? '',
    nivel: f.nivel,
    prova: (f.prova ?? 'pessoa') as TipoDeProva,
    estado: (f.estado ?? 'falta') as EstadoDoItem,
    pendente: f.pendente,
    motivo: f.motivo ?? null,
    fontes: f.fontes ?? [],
    onde: (f.onde ?? []).map((o) => ({ frente: o.frente ?? null, passo: o.passo ?? null, rotulo: o.rotulo ?? '' })),
    chavesDaCuradoria: f.chavesDaCuradoria ?? [],
    passo: f.passo,
    caixinhaEm: f.caixinha ? new Date(f.caixinha.em) : null,
    caixinhaPor: f.caixinha?.nome ?? null,
    aceite: f.aceite ? { motivo: f.aceite.motivo ?? '', em: new Date(f.aceite.em) } : null,
    camada: (f.camada ?? 'captacao') as Camada,
    opcional: f.opcional,
    sugestoes: (f.sugestoes ?? []).map((s) => ({
      valor: s.valor ?? null,
      documento: s.documento ?? '',
      rotulo: s.rotulo ?? null,
      campoId: s.campoId ?? null,
      ticketId: s.ticketId ?? null,
    })),
    faltaDesde: f.faltaDesde ? new Date(f.faltaDesde) : null,
  };
}

export function mapearCompletude(fio: Fio): Completude {
  return {
    nivelUmCompleto: fio.nivelUmCompleto,
    nivelDoisCompleto: fio.nivelDoisCompleto,
    faltam: fio.faltam,
    nivelProvisorio: fio.nivelProvisorio,
    itens: (fio.itens ?? []).map(mapearItem),
    frentes: (fio.frentes ?? []).map((f) => ({
      frente: f.frente ?? '',
      rotulo: f.rotulo ?? f.frente ?? '',
      estado: f.estado ?? 'aberta',
      passoDaVez: f.passoDaVez,
      totalDePassos: f.totalDePassos,
      itensFaltando: f.itensFaltando,
      passosPorFazer: f.passosPorFazer,
    })),
    checklist: { versao: fio.checklist.versao ?? null, configurado: fio.checklist.configurado },
  };
}

export function completudeDoCaso(casoId: string) {
  return queryOptions({
    queryKey: chavesDeCompletude.doCaso(casoId),
    queryFn: ({ signal }) =>
      requisitar<Fio>(`/casos/${encodeURIComponent(casoId)}/completude`, { sinal: signal }).then(mapearCompletude),
    ...AO_VIVO,
  });
}

/**
 * `POST /casos/{id}/completude/{chave}/aceitar-sem-prova`. Motivo obrigatório (`400`); item já provado ou que se fecha
 * pela caixinha volta `409`. O ator NÃO vai no corpo: o back o lê da sessão.
 */
export function aceitarSemProva(casoId: string, chave: string, motivo: string): Promise<Completude> {
  return requisitar<Fio>(
    `/casos/${encodeURIComponent(casoId)}/completude/${encodeURIComponent(chave)}/aceitar-sem-prova`,
    { method: 'POST', corpo: { motivo } },
  ).then(mapearCompletude);
}

/**
 * O check do valor que o e-mail trouxe (`TP-072`): a mesma confirmação da triagem (`PATCH /triagem/{ticket}/campos/{campo}`),
 * com o valor como veio. Confirmado, o campo passa a provar o item.
 */
export function confirmarValorDoEmail(ticketId: string, campoId: string, valor: string): Promise<unknown> {
  return requisitar(`/triagem/${encodeURIComponent(ticketId)}/campos/${encodeURIComponent(campoId)}`, {
    method: 'PATCH',
    corpo: { valorHumano: valor },
  });
}
